import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import IosInstallBanner from "@/components/IosInstallBanner";
import {
  LayoutDashboard, Package, FolderTree, Users as UsersIcon,
  AlertTriangle, History, LogOut, Bell, ShoppingBag, CalendarDays,
  BarChart3, Wallet, ClipboardList, Ticket, Menu, X, Truck, Receipt, FileSpreadsheet,
  Camera, Banknote, Layers,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/products", label: "Products", icon: Package, testid: "nav-products" },
  { to: "/orders", label: "Orders", icon: ShoppingBag, testid: "nav-orders" },
  { to: "/daily-sales", label: "Daily Sales", icon: CalendarDays, testid: "nav-daily-sales" },
  { to: "/purchase-orders", label: "Purchase Orders", icon: ClipboardList, testid: "nav-purchase-orders" },
  { to: "/vendors", label: "Vendors", icon: Truck, testid: "nav-vendors" },
  { to: "/dtf-sheets", label: "DTF Sheets", icon: Layers, testid: "nav-dtf-sheets" },
  { to: "/collections", label: "Collections", icon: Receipt, testid: "nav-collections" },
  { to: "/attendance", label: "Attendance", icon: Camera, testid: "nav-attendance" },
  { to: "/salary", label: "Salary Slips", icon: Banknote, testid: "nav-salary" },
  { to: "/tasks", label: "Task Ledger", icon: Ticket, testid: "nav-tasks" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/gst-report", label: "GST Report", icon: FileSpreadsheet, testid: "nav-gst-report" },
  { to: "/finance", label: "Finance", icon: Wallet, testid: "nav-finance" },
  { to: "/categories", label: "Categories", icon: FolderTree, testid: "nav-categories" },
  { to: "/low-stock", label: "Low Stock", icon: AlertTriangle, testid: "nav-low-stock" },
  { to: "/movements", label: "Movements", icon: History, testid: "nav-movements" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Auto-close mobile drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Lock body scroll when drawer is open on mobile
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [drawerOpen]);

  async function onLogout() {
    await logout();
    nav("/login", { replace: true });
  }

  const currentTitle = NAV.find((n) => n.end ? location.pathname === n.to : location.pathname.startsWith(n.to) && n.to !== "/")?.label
    || (location.pathname === "/" ? "Dashboard" : "");

  const sidebarInner = (
    <>
      <div className="h-2 bg-[#7A3E1F] border-b-2 border-[#2F343A]" />
      <div className="p-4 sm:p-5 border-b-2 border-[#2F343A] flex items-center gap-3">
        <img src="/brand/closset-logo.webp" alt="Closet Apparels" className="w-11 h-11 border-2 border-[#2F343A] bg-white object-contain p-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="font-display font-black text-[13px] tracking-tight text-[#2F343A] truncate">CLOS<span className="text-[#7A3E1F]">E</span>T APPARELS</div>
          <div className="font-mono-ds text-[9px] tracking-widest text-zinc-500">INVENTORY OS</div>
        </div>
        {/* Close button visible only when drawer is open on mobile */}
        <button
          className="lg:hidden ml-auto p-1.5 border-2 border-[#2F343A] shrink-0"
          onClick={() => setDrawerOpen(false)}
          aria-label="Close menu"
          data-testid="close-drawer-btn"
        >
          <X size={16} />
        </button>
      </div>

      <nav className="flex-1 py-3 overflow-y-auto">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
          >
            <n.icon size={16} strokeWidth={2.5} /> {n.label}
          </NavLink>
        ))}
        {user?.role === "admin" && (
          <NavLink to="/users" data-testid="nav-users" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}>
            <UsersIcon size={16} strokeWidth={2.5} /> Users
          </NavLink>
        )}
        {user?.role === "admin" && (
          <NavLink to="/settings" data-testid="nav-settings" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}>
            <Bell size={16} strokeWidth={2.5} /> Notifications
          </NavLink>
        )}
      </nav>

      <div className="border-t-2 border-black p-4">
        <div className="mb-3">
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">Signed in</div>
          <div className="font-display font-bold text-sm truncate" data-testid="current-user-name">{user?.name}</div>
          <div className="text-xs text-zinc-600 truncate" data-testid="current-user-email">{user?.email}</div>
          <span className="brut-badge mt-2" data-testid="current-user-role">{user?.role}</span>
        </div>
        <button
          data-testid="logout-btn"
          onClick={onLogout}
          className="brut-btn-outline w-full text-xs py-2 flex items-center justify-center gap-2"
        >
          <LogOut size={14} /> Logout
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-white lg:flex">
      {/* Mobile top bar (visible only on <lg screens) */}
      <header className="lg:hidden sticky top-0 z-40 border-b-2 border-[#2F343A] bg-white flex items-center gap-3 px-3 py-2.5" data-testid="mobile-topbar">
        <button
          className="p-2 border-2 border-[#2F343A] shadow-[3px_3px_0_0_#2F343A] bg-white active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open menu"
          data-testid="open-drawer-btn"
        >
          <Menu size={18} strokeWidth={2.5} />
        </button>
        <img src="/brand/closset-logo.webp" alt="Closet Apparels" className="w-8 h-8 border-2 border-[#2F343A] bg-white object-contain p-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-display font-black text-[13px] tracking-tight text-[#2F343A] leading-none">CLOS<span className="text-[#7A3E1F]">E</span>T APPARELS</div>
          <div className="font-mono-ds text-[9px] tracking-widest text-zinc-500 leading-none mt-0.5 truncate">
            {currentTitle ? currentTitle.toUpperCase() : "INVENTORY OS"}
          </div>
        </div>
      </header>

      {/* Desktop sidebar (always visible on lg+) */}
      <aside className="hidden lg:flex w-64 border-r-2 border-black flex-col sticky top-0 h-screen shrink-0" data-testid="sidebar">
        {sidebarInner}
      </aside>

      {/* Mobile drawer overlay */}
      {drawerOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/60 z-40"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
            data-testid="drawer-backdrop"
          />
          <aside
            className="lg:hidden fixed left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-white border-r-2 border-[#2F343A] shadow-[6px_0_0_0_#2F343A] z-50 flex flex-col"
            data-testid="mobile-drawer"
          >
            {sidebarInner}
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0 lg:min-h-screen">
        <Outlet />
      </main>
      <IosInstallBanner />
    </div>
  );
}
